import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { Spin } from 'antd';
import { exchangeCodeForToken } from 'modules/auth/services/EmployeeAuthService';
import * as EmpAuthService from 'modules/auth/services/EmployeeAuthService';
import * as PlanService from 'modules/auth/services/PlanService';
import { handleErrorCode } from 'modules/auth/util/error';
import {
  setEmployeeModal,
  setInProgress,
  setDependentDetails,
  setAuthView,
  fetchEmployeeLogin,
} from 'modules/auth/slices/employeeAuthSlice';
import { AUTH_VIEW, PLAN_VIEW } from 'modules/auth/constants/authConstants';
import {
  setElectionData,
  setOnboardingView,
} from 'modules/auth/slices/idCardSlice';
import styles from './oAuthCallback.module.less';

const OAuthCallback: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        dispatch(setInProgress(true));

        // Get the authorization code from URL query params
        const code = searchParams.get('code');

        // Extract the benguide_url parameter from the callback URL
        let benguideUrl = searchParams.get('benguide_url');
        if (!benguideUrl) {
          // If no benguide_url is found, navigate to the default route
          benguideUrl = localStorage.getItem('benguideUrl');
        }

        if (!code) {
          console.error('No authorization code found in callback URL');
          navigate(`${benguideUrl}`);
          return;
        }

        // Get code_verifier from localStorage (was stored during login initiation)
        const codeVerifier = localStorage.getItem('code_verifier');
        const planYearId = localStorage.getItem('planYearId') || ''; // Store this during login initiation

        if (!codeVerifier) {
          console.error('No code verifier found in localStorage');
          navigate(`${benguideUrl}`);
          return;
        }

        // Exchange authorization code for access token
        const tokenResponse = await exchangeCodeForToken(code, codeVerifier);
        if (tokenResponse.status === 200) {
          const individualId = tokenResponse?.data?.individualId;
          const employeeId = tokenResponse?.data?.employeeId;
          const hasElectionResponse = await PlanService.hasElections(
            individualId,
            planYearId
          );
          dispatch(fetchEmployeeLogin(planYearId));

          if (hasElectionResponse) {
            // dispatch(setEmployee(tokenResponse?.data));
            dispatch(setElectionData(hasElectionResponse?.data));

            if (hasElectionResponse.data?.hasPlans) {
              dispatch(setOnboardingView(PLAN_VIEW.CARD.type));

              if (
                tokenResponse?.data?.isDep === false &&
                !isEmpty(tokenResponse?.data?.dependentIds)
              ) {
                const dependentsRes = await EmpAuthService.getDependentsDetails(
                  employeeId,
                  planYearId
                );
                dispatch(setDependentDetails(dependentsRes?.data));
              }
            } else {
              dispatch(setOnboardingView(PLAN_VIEW.ON_BOARDING.type));
            }
          }

          // Set the auth view to the appropriate next step
          dispatch(setAuthView(AUTH_VIEW.LOGIN.type));
        }

        // Navigate back to the homepage or previous page
        navigate(`${benguideUrl}`);
        // Open the auth modal again
        dispatch(setEmployeeModal(true));
      } catch (error) {
        console.error('Error processing OAuth callback:', error);
        navigate(`${localStorage.getItem('benguideUrl')}`);
        // Open the auth modal again
        dispatch(setEmployeeModal(true));
        handleErrorCode(dispatch, error);
      } finally {
        dispatch(setInProgress(false));
      }
    };

    handleOAuthCallback();
  }, [dispatch, navigate, searchParams]);

  return (
    <div className="oauth-callback-container">
      <div className={styles.loadingIndicator}>
        <Spin size="large" />
        <p className={styles.loadingText}>Completing authentication...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
